import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import AppButton from '../../../../components/designe/app-button/AppButton';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import AppDialog from '../../../../components/reusable/app-dialog/AppDialog';
import FlexibleColumnsTable from '../../../../components/reusable/Table/FlexibleColumnsTable';
import Search from '../../../../components/reusable/search/Search';
import { useAccess } from '../../../../hooks/useAccess';
import {
  useCreateNotificationTemplateMutation,
  useGetNotificationTemplatesQuery,
} from '../../../../services/redux/api/notificationsAdminApi';
import {
  NOTIFICATION_DISPLAY_DOMAINS,
  type NotificationDisplayDomain,
  type NotificationPreferenceGroup,
  type NotificationTemplateCategory,
} from '../../../../types/interfaces/notification-admin';
import {
  isCategoryPreferenceConsistent,
  preferenceGroupsForCategory,
} from '../../../../utils/notification-admin-rules';
import {
  notificationCategoryLabel,
  notificationDisplayDomainLabel,
  notificationPreferenceGroupLabel,
} from '../../../../utils/notification-template-labels';
import theme from '../../../../styles/theme';
import { useNotificationsTableStyles } from '../NotificationsTable.styles';
import { notificationsBasePath } from '../notificationsBasePath';
import { NotificationTemplatesFilter } from './NotificationTemplatesFilter';
import { useNotificationTemplatesListComponent } from './useNotificationTemplatesListComponent';

export function NotificationTemplatesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { classes } = useNotificationsTableStyles(theme);
  const { access } = useAccess();
  const canMutate =
    (access?.notifications?.canCreate || access?.notifications?.canEdit) ??
    false;
  const canDelete = access?.notifications?.canDelete ?? false;

  const { data: templates = [], isLoading } =
    useGetNotificationTemplatesQuery();
  const [createTemplate, { isLoading: creating }] =
    useCreateNotificationTemplateMutation();

  const onOpenRow = useCallback(
    (row) => navigate(`${notificationsBasePath}/templates/${row.id}`),
    [navigate],
  );

  const {
    sortedArray,
    columnsList,
    order,
    orderBy,
    cellReducers,
    onSort,
    onSortColumns,
    filter,
    setFilter,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filterFormik,
    filterActiveOptions,
    categoryOptions,
    preferenceOptions,
  } = useNotificationTemplatesListComponent(
    classes,
    templates,
    onOpenRow,
    canDelete,
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [code, setCode] = useState('');
  const [category, setCategory] =
    useState<NotificationTemplateCategory>('marketing');
  const [preferenceGroup, setPreferenceGroup] =
    useState<NotificationPreferenceGroup>('marketing');
  const [displayDomain, setDisplayDomain] =
    useState<NotificationDisplayDomain>('generic');
  const [activeNew, setActiveNew] = useState(true);

  const onCategoryChange = (c: NotificationTemplateCategory) => {
    setCategory(c);
    const allowed = preferenceGroupsForCategory(c);
    if (c === 'marketing') {
      setPreferenceGroup('marketing');
    } else if (!allowed.includes(preferenceGroup)) {
      setPreferenceGroup(allowed[0]);
    }
  };

  const submitCreate = async () => {
    const resolvedPreference =
      category === 'marketing' ? 'marketing' : preferenceGroup;
    if (!isCategoryPreferenceConsistent(category, resolvedPreference)) return;
    const res = await createTemplate({
      code: code.trim(),
      category,
      preferenceGroup: resolvedPreference,
      displayDomain,
      active: activeNew,
    }).unwrap();
    setCreateOpen(false);
    setCode('');
    if (res?.id) {
      navigate(`${notificationsBasePath}/templates/${res.id}`);
    }
  };

  const titleNode = useMemo(
    () => (
      <Typography variant="h6" gutterBottom>
        {t('adminDashboard.notifications.templates.title')}
      </Typography>
    ),
    [t],
  );

  if (isLoading) {
    return (
      <div className={classes.wrapper}>
        {titleNode}
        <Typography>{t('adminDashboard.notifications.loading')}</Typography>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      {titleNode}
      <NotificationTemplatesFilter
        formik={filterFormik}
        filterActiveOptions={filterActiveOptions}
        categoryOptions={categoryOptions}
        preferenceOptions={preferenceOptions}
      />
      <Box display="flex" gap={1} alignItems="center" marginBottom={2}>
        <Search fullWidth value={filter} onChange={setFilter} />
        {canMutate && (
          <AppButton onClick={() => setCreateOpen(true)}>
            {t('common.add')}
          </AppButton>
        )}
      </Box>
      <FlexibleColumnsTable
        data={sortedArray}
        columnsList={columnsList}
        order={order}
        orderBy={orderBy}
        onSort={onSort}
        classes={classes}
        onSortColumns={onSortColumns}
        cellReducers={cellReducers}
        onSelectAllRows={() => {}}
        stickyHeader={true}
      />
      <ConfirmDialog
        open={!!itemForDelete}
        onClose={() => setItemForDelete(null)}
        title={t('common.delete')}
        onSubmit={() => onItemDelete()}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('adminDashboard.notifications.templates.createTitle')}>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ minWidth: 320 }}>
          <TextField
            label={t('adminDashboard.notifications.templates.code')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="nc-cat">
              {t('adminDashboard.notifications.templates.category')}
            </InputLabel>
            <Select
              labelId="nc-cat"
              label={t('adminDashboard.notifications.templates.category')}
              value={category}
              onChange={(e) =>
                onCategoryChange(e.target.value as NotificationTemplateCategory)
              }>
              <MenuItem value="marketing">
                {notificationCategoryLabel(t, 'marketing')}
              </MenuItem>
              <MenuItem value="transactional">
                {notificationCategoryLabel(t, 'transactional')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="nc-pref">
              {t('adminDashboard.notifications.templates.preferenceGroup')}
            </InputLabel>
            <Select
              labelId="nc-pref"
              label={t(
                'adminDashboard.notifications.templates.preferenceGroup',
              )}
              value={preferenceGroup}
              onChange={(e) =>
                setPreferenceGroup(
                  e.target.value as NotificationPreferenceGroup,
                )
              }>
              {preferenceGroupsForCategory(category).map((p) => (
                <MenuItem key={p} value={p}>
                  {notificationPreferenceGroupLabel(t, p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="nc-domain">
              {t('adminDashboard.notifications.templates.displayDomain')}
            </InputLabel>
            <Select
              labelId="nc-domain"
              label={t('adminDashboard.notifications.templates.displayDomain')}
              value={displayDomain}
              onChange={(e) =>
                setDisplayDomain(e.target.value as NotificationDisplayDomain)
              }>
              {NOTIFICATION_DISPLAY_DOMAINS.map((d) => (
                <MenuItem key={d} value={d}>
                  {notificationDisplayDomainLabel(t, d)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              checked={activeNew}
              onChange={(e) => setActiveNew(e.target.checked)}
            />
            <Typography variant="body2">
              {t('adminDashboard.notifications.templates.active')}
            </Typography>
          </Box>
          <AppButton
            onClick={submitCreate}
            disabled={
              creating ||
              !code.trim() ||
              !isCategoryPreferenceConsistent(
                category,
                category === 'marketing' ? 'marketing' : preferenceGroup,
              )
            }>
            {t('common.save')}
          </AppButton>
        </Box>
      </AppDialog>
    </div>
  );
}
